/**
 * Whether the app authenticates via the RsConnect IDP (.NET data layer) rather
 * than Firebase Auth/Firestore. Single source of truth for the auth-mode switch
 * so the providers, layout, and routing can never disagree about which data
 * layer is mounted.
 */
export const idpEnabled = Boolean(import.meta.env.VITE_IDP_ISSUER_URL);

/**
 * The IDP's own forgot-password page.
 *
 * Under `idpEnabled` the password lives in RsConnect, not Firebase, so the app
 * cannot send the mail itself. It cannot call the IDP's
 * `POST /api/account/forgot-password` from the browser either: RsConnect's
 * production CORS allowlist covers app.akui.ai and app.akkordly.dk only, so a
 * cross-origin POST from app.mesterplan.dk is blocked. Hand the user to the
 * IDP page instead — the same flow the sign-in screen links to. Mirrors
 * Akkordly's profile page.
 */
export function idpForgotPasswordUrl(): string {
  const issuer = import.meta.env.VITE_IDP_ISSUER_URL ?? '';
  return `${issuer.replace(/\/?$/, '/')}forgot-password`;
}

/**
 * The IDP's email-confirmation page, prefilled with the address to confirm. Used by the
 * "Bekræft din email for at få adgang til <firma>" screen: the owner of the address gets a code
 * from RsConnect and confirms there.
 */
export function idpVerifyEmailUrl(email: string | null | undefined): string {
  const issuer = import.meta.env.VITE_IDP_ISSUER_URL ?? '';
  const base = `${issuer.replace(/\/?$/, '/')}verify-email?product=mesterplan`;
  return email ? `${base}&email=${encodeURIComponent(email)}` : base;
}

/**
 * Sends the browser to the IDP's forgot-password page.
 *
 * A popup blocker makes `window.open` return null (or throw), so fall back to
 * navigating the current tab — otherwise the click silently does nothing, which
 * is the exact failure mode being fixed on the admin screens (#2161) and the
 * profile page before it (#2175).
 *
 * Reports which of three things happened, so callers can say the right thing:
 *
 * - `opened`      a window came back, i.e. best-effort staying put (not a
 *                 guarantee — a blocker can still close the tab afterwards).
 *                 Safe to show the instruction toast.
 * - `redirecting` the popup was blocked and this tab is navigating instead. Show
 *                 nothing: a toast fired here never renders, which was a known
 *                 cosmetic bug shipped with #2175.
 * - `blocked`     neither worked (a sandboxed frame can refuse both). Callers
 *                 must surface an error, or the click is the silent no-op this
 *                 whole change exists to remove.
 */
export type IdpForgotPasswordOutcome = 'opened' | 'redirecting' | 'blocked';

export function openIdpForgotPassword(): IdpForgotPasswordOutcome {
  const url = idpForgotPasswordUrl();

  // A popup blocker usually makes window.open return null, but some browsers
  // throw SecurityError/InvalidAccessError synchronously instead. Treat both as
  // blocked, so the click is never a silent no-op.
  let opened: Window | null = null;
  try {
    opened = window.open(url, '_blank', 'noopener,noreferrer');
  } catch {
    opened = null;
  }
  if (opened) {
    return 'opened';
  }

  try {
    window.location.assign(url);
    return 'redirecting';
  } catch {
    return 'blocked';
  }
}
